import { useRef } from 'react'
import {
  addToaster,
  Attachment,
  bulkUploadAPI,
  ErrorResponse,
  hideLoader,
  showLoader,
  uploadFileToS3Function,
} from '@app-store'
import { v4 as uuid } from 'uuid'
import {
  CustomButton,
  detectPasswordProtectedFile,
  PASSWORD_PROTECTED_TOASTER_MSG,
  isFileSignatureConsistent,
  NAMESPACE,
  SvgUpload,
  validateFileName,
  checkFileExtensionPresent,
  FILE_NAME_VALIDATION_ERRORS,
  getFileExtension,
  getAcceptedExtensionsString,
  isValidFileType,
  getUnsupportedFileTypeMessage,
} from '@ui-kit'
import { useAppDispatch, useAppSelector } from '../../utils/agent.store'
import { COMM_TEMPLATE, NAMESPACES } from '../../utils/constants'
import { AxiosError } from 'axios'
import { isFileSizeValid } from '../../utils/helperFunctions'

export interface MiscUploadButtonProps {
  setAttachmentsKey: React.Dispatch<
    React.SetStateAction<Attachment[] | undefined>
  >
  setAttachments: React.Dispatch<React.SetStateAction<Attachment[]>>
  tabIndex?: number
}

const MiscUploadButton: React.FC<MiscUploadButtonProps> = ({
  setAttachmentsKey,
  setAttachments,
  tabIndex,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null)

  const dispatch = useAppDispatch()
  const selectedTenantId = useAppSelector(
    (state) => state?.authUser?.selectedClientTenant?.id
  )

  const handleUploadClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click()
    }
  }

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.length) {
      const selectedFile = e.target.files[0]
      const fileExtension = getFileExtension(selectedFile.name)
      const fileMetadata = [
        {
          fileName: selectedFile.name,
          size: selectedFile.size,
          fileType: selectedFile?.type?.replace('application/', ''),
        },
      ]

      if (!checkFileExtensionPresent(selectedFile.name)) {
        dispatch(
          addToaster({
            message: `${
              FILE_NAME_VALIDATION_ERRORS.NO_EXTENSION_FOUND
            } ${getAcceptedExtensionsString()}`,
            type: 'error',
            id: uuid(),
          })
        )
        if (fileInputRef.current) {
          fileInputRef.current.value = ''
        }
        return
      }

      const invalidFile = !isValidFileType(selectedFile)
      if (invalidFile) {
        dispatch(
          addToaster({
            message: getUnsupportedFileTypeMessage(
              fileExtension,
              getAcceptedExtensionsString()
            ),
            type: 'error',
            id: uuid(),
          })
        )
        if (fileInputRef.current) {
          fileInputRef.current.value = ''
        }
        return
      }

      const passwordResult = await detectPasswordProtectedFile(selectedFile)
      if (passwordResult === 'blocked_password') {
        dispatch(
          addToaster({
            message: PASSWORD_PROTECTED_TOASTER_MSG,
            type: 'error',
            id: uuid(),
          })
        )
        if (fileInputRef.current) {
          fileInputRef.current.value = ''
        }
        return
      }

      const hasValidSignature = await isFileSignatureConsistent(selectedFile)
      if (!hasValidSignature) {
        dispatch(
          addToaster({
            message: NAMESPACE.INVALID_FILE_SIGNATURE_TOASTER_MSG,
            type: 'error',
            id: uuid(),
          })
        )
        if (fileInputRef.current) {
          fileInputRef.current.value = ''
        }
        return
      }

      const oversizedFile = !isFileSizeValid(selectedFile)
      if (oversizedFile) {
        dispatch(
          addToaster({
            message: NAMESPACES.FILE_SIZE_EXCEEDS_MSG,
            type: 'error',
            id: uuid(),
          })
        )
        if (fileInputRef.current) {
          fileInputRef.current.value = ''
        }
        return
      }

      const fileNameValidationResult = validateFileName(selectedFile.name)
      if (!fileNameValidationResult.isValid) {
        dispatch(
          addToaster({
            message: fileNameValidationResult.error || '',
            type: 'error',
            id: uuid(),
          })
        )
        if (fileInputRef.current) {
          fileInputRef.current.value = ''
        }
        return
      }

      try {
        dispatch(showLoader())

        const response = await bulkUploadAPI(
          fileMetadata,
          selectedTenantId,
          true
        )
        const uploadedFile = response?.data?.[0]
        const presignedUrl = uploadedFile.url
        const id = uploadedFile.id
        if (selectedFile)
          await uploadFileToS3Function({
            presignedUrl,
            selectedFile,
            tagging: uploadedFile?.tagging ?? undefined,
            documentIdentifierMetaData:
              uploadedFile?.DOCUMENT_IDENTIFIER ?? undefined,
            uploadChannel: uploadedFile?.UPLOAD_CHANNEL ?? undefined,
            uploadIdentifier: uploadedFile?.UPLOAD_IDENTIFIER ?? undefined,
          })
        const attachmentObject: Attachment = {
          unique_attachment_name: uploadedFile.fileName,
          attachment_name: uploadedFile.userFileName,
          attachment_document_type: COMM_TEMPLATE.STATIC_DOCUMENT_UPPERCASE,
          attachment_type: 'CUSTOM',
          attachment_document_id: id,
          include_editable_text: false,
          contains_docusign: false,
          path: uploadedFile.path,
        }
        setAttachmentsKey((prev) => [...(prev || []), attachmentObject])
        setAttachments((prev) => [...(prev || []), attachmentObject])

        dispatch(
          addToaster({
            message: COMM_TEMPLATE?.FILE_UPLOADED_SUCCESSFULLY,
            type: 'success',
            id: uuid(),
          })
        )
      } catch (error) {
        const response = (error as AxiosError)?.response?.data as ErrorResponse
        dispatch(
          addToaster({
            message: response?.message ?? COMM_TEMPLATE?.ERR_UPLOADING_FILE,
            type: 'error',
            id: uuid(),
          })
        )
      } finally {
        dispatch(hideLoader())
        if (fileInputRef.current) {
          fileInputRef.current.value = ''
        }
      }
    }
  }

  return (
    <div className="flex items-center justify-center w-[7.438rem]">
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        style={{ display: 'none' }}
        accept={getAcceptedExtensionsString()}
      />
      <CustomButton
        type="button"
        onClick={handleUploadClick}
        color="primary"
        widthClass="w-fit"
        tabIndex={tabIndex}
      >
        <div className="flex items-center justify-between gap-2">
          <SvgUpload className="svg-path-fill-accent-900" />
          <span>{COMM_TEMPLATE?.UPLOAD}</span>
        </div>
      </CustomButton>
    </div>
  )
}

export default MiscUploadButton
